/** Word-level diff for highlighting copy changes in personalization preview. */

export type DiffSegment = { type: 'same' | 'remove' | 'add'; text: string };

function tokenizeWords(text: string): string[] {
  const parts = text.match(/\S+|\s+/g);
  return parts ?? (text ? [text] : []);
}

/** LCS on word tokens; returns edit script for the "after" side. */
function lcsDiff(before: string, after: string): DiffSegment[] {
  const a = tokenizeWords(before);
  const b = tokenizeWords(after);
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    Array(m + 1).fill(0),
  );

  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] =
        a[i] === b[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const out: DiffSegment[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ type: 'same', text: a[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ type: 'remove', text: a[i] });
      i++;
    } else {
      out.push({ type: 'add', text: b[j] });
      j++;
    }
  }
  while (i < n) {
    out.push({ type: 'remove', text: a[i++] });
  }
  while (j < m) {
    out.push({ type: 'add', text: b[j++] });
  }
  return out;
}

/** Segments to render the "before" column (same + removed). */
export function diffBeforeSegments(before: string, after: string): DiffSegment[] {
  return lcsDiff(before, after).filter((s) => s.type !== 'add');
}

/** Segments to render the "after" column (same + added). */
export function diffAfterSegments(before: string, after: string): DiffSegment[] {
  return lcsDiff(before, after).filter((s) => s.type !== 'remove');
}

export function htmlToPlainText(html: string): string {
  if (!html.trim()) return '';
  if (typeof DOMParser === 'undefined') {
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}
